const { debounce, viewportSize } = window.BAO.Utils

class QuickBuy extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTouch = false
    this.mouseDown = false

    this.touchstartY = 0
    this.touchendY = 0

    this.shoppableProducts = document.querySelector('shoppable-products')
  }

  static get observedAttributes () {
    return ['data-content']
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (name === 'data-content' && newValue === 'quick-buy-bis') {
      this.reInit()
    }
  }

  setupListeners () {
    super.setupListeners()

    this.setupModal = this.setupModal.bind(this)
    this.handleTrigger = this.handleTrigger.bind(this)
    this.showSuccessMessage = this.showSuccessMessage.bind(this)
    this.handleClose = this.handleClose.bind(this)

    this.listeners.add(window, `load`, this.setupModal)
    this.listeners.add(document, `click`, this.handleTrigger)
    this.els.close.elements.forEach(closeEl => {
      this.listeners.add(closeEl, `click`, this.handleClose)
    })

    this.listeners.add(document, 'drawerOpen', this.handleClose)

    this.detectTouch = this.detectTouch.bind(this)

    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)

    if (this.els?.handle?.exists) {
      this.els.handle.elements.forEach(handle => {
        this.listeners.add(handle, 'touchstart', this.onTouchStart)
        this.listeners.add(handle, 'touchmove', this.onTouchMove)
        this.listeners.add(handle, 'touchend', this.onTouchEnd)

        this.listeners.add(handle, 'mousedown', this.onMouseDown)
        this.listeners.add(document, 'mousemove', this.onMouseMove)
        this.listeners.add(document, 'mouseup', this.onMouseUp)
      })
    }

    this.listeners.add(
      document.documentElement,
      'keydown',
      this.handleKeydown.bind(this),
    )

    this.listeners.add(
      window,
      'resize',
      debounce(this.handleResize.bind(this), 100),
    )
  }

  detectTouch () {
    this.isTouch = true
  }

  onTouchStart (event) {
    this.touchstartY = event.changedTouches[0].screenY

    this.setAttribute('drag', '')
  }

  onTouchMove (event) {
    const diffY = event.changedTouches[0].screenY - this.touchstartY

    if (diffY <= 0) return

    this.style.setProperty('--Drag', `${diffY}px`)
  }

  onTouchEnd (event) {
    this.touchendY = event.changedTouches[0].screenY

    this.style.setProperty('--Drag', `0px`)

    this.removeAttribute('drag')

    if (this.touchendY > this.touchstartY) {
      this.handleClose()
    }
  }

  onMouseDown (event) {
    if (this.isTouch === true) return

    this.mouseDown = true

    this.touchstartY = event.screenY

    this.setAttribute('drag', '')
  }

  onMouseMove (event) {
    if (this.isTouch === true) return

    if (this.mouseDown === false) return

    const diffY = event.screenY - this.touchstartY

    if (diffY <= 0) return

    this.style.setProperty('--Drag', `${diffY}px`)
  }

  onMouseUp (event) {
    if (this.isTouch === true) return

    if (this.mouseDown === false) return

    this.mouseDown = false

    this.touchendY = event.screenY

    this.style.setProperty('--Drag', `0px`)

    this.removeAttribute('drag')

    if (this.touchendY > this.touchstartY) {
      this.handleClose()
    }
  }

  reInit () {
    this.setupListeners()
  }

  setupModal () {
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.showSuccessMessage,
    )

    this.addEventListener(`click`, e => {
      if (e.target !== this) return

      this.setAttribute('aria-hidden', 'true')
    })
  }

  async handleTrigger (event) {
    const trigger = event.target.closest(
      '[data-quick-buy-external-el="trigger"]',
    )

    if (!trigger) return

    // Ensure shoppable product modal is closed
    this.shoppableProducts?.setAttribute('aria-hidden', 'true')

    if (trigger.hasAttribute('data-quick-buy-reopen')) {
      this.reopen()
      return
    }

    this.triggerUrl = trigger.dataset.productUrl

    if (
      !trigger.hasAttribute('data-bis') ||
      (trigger.hasAttribute('data-bis') &&
        trigger.getAttribute('data-bis') === 'false')
    ) {
      this.setAttribute('aria-hidden', 'true')
    } else {
      // Prevent the form from submitting and triggering a page reload.
      event.preventDefault()
    }

    const sectionName = trigger.dataset.sectionName

    try {
      const res = await window.BAO.sectionRenderer.fetchSingle(
        sectionName,
        trigger.dataset.productUrl,
      )

      const sectionId = `shopify-section-${sectionName}`

      const sectionHTML = window.BAO.sectionRenderer.getOuterHtml(res, {
        id: sectionId,
      })

      this.els.content.element.innerHTML = sectionHTML
      this.els.content.element.setAttribute('aria-hidden', 'false')

      if (trigger.hasAttribute('data-bis')) {
        const bisFormElem = this.els.content.element.querySelector(
          '[data-product-back-in-stock-form]',
        )

        bisFormElem.setAttribute('data-variant-id', trigger.dataset.variantId)
        bisFormElem.setAttribute(
          'data-variant-title',
          trigger.dataset.variantTitle,
        )
        bisFormElem.setAttribute('data-product-id', trigger.dataset.productId)
        bisFormElem.setAttribute('aria-hidden', 'false')

        const title = bisFormElem.querySelector('[data-title]')
        title.textContent = `${title.textContent} '${trigger.dataset.variantTitle}'`
      }

      this.els['success-message'].element.innerHTML = ''
      this.els.success.element.setAttribute('aria-hidden', 'true')

      window.drawers.activeDrawerKey = ''
      this.setAttribute('aria-hidden', 'false')

      this.fixBody()

      this.dataset.content = sectionName

      this.checkQuickBuySelectedVariant()

      window.setTimeout(() => {
        const isButton =
          this.els.close?.element?.getAttribute('type') === 'button'
        const firstFocusable = this.els.content.element
          .querySelectorAll('a, button, input:not([type=hidden])')
          ?.item(1)

        if (isButton) {
          this.els.close.element.focus()
        } else if (firstFocusable) {
          firstFocusable.focus()
        }
      }, 500)
    } catch (error) {
      this.setAttribute('aria-hidden', 'true')
    }
  }

  reopen () {
    this.els['success-message'].element.innerHTML = ''
    this.els.success.element.setAttribute('aria-hidden', 'true')

    window.drawers.activeDrawerKey = ''

    this.fixBody()

    this.setAttribute('aria-hidden', 'false')

    this.checkQuickBuySelectedVariant()

    window.setTimeout(() => {
      const isButton =
        this.els.close?.element?.getAttribute('type') === 'button'
      const firstFocusable = this.els.content.element
        .querySelectorAll('button, input:not([type=hidden])')
        ?.item(1)

      if (isButton) {
        this.els.close.element.focus()
      } else if (firstFocusable) {
        firstFocusable.focus()
      }
    }, 500)
  }

  checkQuickBuySelectedVariant () {
    if (this.dataset.content !== 'product-quick-buy') return

    const variantId =
      new URLSearchParams(window.location.search)?.get('variant') || null
    if (!variantId) return

    const matchingVariantRadioEl = Array.from(
      this.querySelectorAll('[data-variant-radios-el="option-choice"]'),
    ).find(el => el.getAttribute('data-variant-id') === variantId)

    if (matchingVariantRadioEl && matchingVariantRadioEl.checked === false) {
      matchingVariantRadioEl.click()
    }
  }

  fixBody () {
    const scrollY = window.scrollY
    const body = document.body
    body.classList.add('util-SiteOverflowed')

    if (!window.BAO.MediaBreakpoints.isMd) {
      body.style.position = 'fixed'
      body.style.top = `-${scrollY}px`
      body.style.maxWidth = '100%'
      body.style.width = '100%'
    }
  }

  releaseBody () {
    const body = document.body
    const scrollY = body.style.top

    if (
      body.classList.contains('util-DrawersOpen') &&
      body.classList.contains('drw-Drawers-cart')
    ) {
      return
    }

    body.classList.remove('util-SiteOverflowed')

    body.style.position = ''
    body.style.top = ''
    body.style.maxWidth = ''
    body.style.width = ''

    if (!window.BAO.MediaBreakpoints.isMd) {
      window.scrollTo({
        top: parseInt(scrollY || '0') * -1,
        left: 0,
        behavior: 'instant',
      })
    }
  }

  showSuccessMessage (e) {
    this.els.modal.element.setAttribute('aria-busy', 'true')

    const message = window.quickBuyStrings.success.replace(
      '[[NAME]]',
      e.detail.state.variant_title,
    )

    this.els['success-message'].element.innerHTML = message

    window.setTimeout(() => {
      this.els.content.element.setAttribute('aria-hidden', 'true')
      this.els.success.element.setAttribute('aria-hidden', 'false')

      this.els.modal.element.setAttribute('aria-busy', 'false')

      window.setTimeout(() => {
        this.handleClose()
      }, 2000)
    }, 500)
  }

  handleClose () {
    this.dataset.content = ''
    this.setAttribute('aria-hidden', 'true')

    this.releaseBody()

    if (document.querySelector(`[data-product-url="${this.triggerUrl}"]`)) {
      document.querySelector(`[data-product-url="${this.triggerUrl}"]`).focus()
    }

    this.triggerUrl = ''
  }

  handleKeydown (e) {
    if (this.triggerUrl === '') return

    if (e.key !== 'Escape') return

    this.handleClose()
  }

  handleResize () {
    if (viewportSize() !== `xs`) {
      this.handleClose()
    }
  }
}

if (!customElements.get('quick-buy')) {
  customElements.define('quick-buy', QuickBuy)
}
